import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { FaMicrosoft } from 'react-icons/fa';
import { Mail, Lock, AlertCircle, ShieldCheck, ArrowLeft } from 'lucide-react';
import { toast } from 'sonner';

const Login = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // MFA step
  const [step, setStep] = useState('credentials'); // 'credentials' | 'mfa'
  const [mfaToken, setMfaToken] = useState(null);
  const [mfaCode, setMfaCode] = useState('');

  const { login, verifyMfa } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const result = await login(email, password);
      if (result.mfaRequired) {
        setMfaToken(result.mfaToken);
        setMfaCode('');
        setStep('mfa');
      } else {
        toast.success('Login realizado com sucesso!');
        navigate('/dashboard');
      }
    } catch (err) {
      const message = err.response?.data?.detail || 'Erro ao processar solicitação';
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const handleMfaSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await verifyMfa(mfaToken, mfaCode);
      toast.success('Login realizado com sucesso!');
      navigate('/dashboard');
    } catch (err) {
      const message = err.response?.data?.detail || 'Código inválido';
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const backToCredentials = () => {
    setStep('credentials');
    setMfaToken(null);
    setMfaCode('');
    setError('');
  };

  const handleSSOClick = () => {
    toast.info('SSO Microsoft será configurado em breve');
  };

  return (
    <div 
      className="min-h-screen flex items-center justify-center relative"
      style={{
        backgroundImage: `url('${process.env.PUBLIC_URL}/bg-login.png')`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat'
      }}
    >
      {/* Overlay for better contrast */}
      <div className="absolute inset-0 bg-black/40" />
      
      {/* Login Card */}
      <div className="relative z-10 w-full max-w-md mx-4">
        <div className="bg-white rounded-lg shadow-2xl p-8 space-y-6">
          {/* Logo */}
          <div className="text-center">
            <img 
              src={`${process.env.PUBLIC_URL}/logo-kn.png`}
              alt="Kuehne+Nagel" 
              className="h-12 w-auto mx-auto mb-4"
            />
            <h1 className="text-xl font-semibold text-gray-900">
              Gestão de Licenças
            </h1>
            <p className="mt-1 text-gray-500 text-sm">
              {step === 'mfa' ? 'Verificação em duas etapas' : 'Entre para acessar o sistema'}
            </p>
          </div>

          {step === 'credentials' && (
            <>
              {/* SSO Button */}
              <button
                type="button"
                onClick={handleSSOClick}
                data-testid="sso-microsoft-btn"
                className="w-full flex items-center justify-center gap-3 bg-white hover:bg-gray-50 text-gray-700 border border-gray-300 h-11 rounded-md transition-all font-medium"
              >
                <FaMicrosoft className="w-5 h-5 text-[#00a4ef]" />
                Entrar com Microsoft
              </button>

              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-200"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-4 bg-white text-gray-500">ou continue com email</span>
                </div>
              </div>
            </>
          )}

          {error && (
            <div className="flex items-center gap-2 p-3 bg-rose-50 border border-rose-200 rounded-md text-rose-700 text-sm" data-testid="login-error">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              {error}
            </div>
          )}

          {step === 'credentials' ? (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-gray-700 font-semibold text-sm">Email</Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    id="email"
                    type="email"
                    placeholder="seu@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-10 h-11 bg-white border-gray-300 text-gray-900 placeholder:text-gray-400 focus:border-[#003366] focus:ring-[#003366]"
                    data-testid="login-email-input"
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-gray-700 font-semibold text-sm">Senha</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    id="password"
                    type="password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-10 h-11 bg-white border-gray-300 text-gray-900 placeholder:text-gray-400 focus:border-[#003366] focus:ring-[#003366]"
                    data-testid="login-password-input"
                    required
                  />
                </div>
              </div>

              <Button
                type="submit"
                disabled={loading}
                data-testid="login-submit-btn"
                className="w-full h-11 bg-[#003366] hover:bg-[#002244] text-white font-semibold mt-2"
              >
                {loading ? 'Entrando...' : 'Entrar'}
              </Button>
            </form>
          ) : (
            <form onSubmit={handleMfaSubmit} className="space-y-4">
              <div className="flex items-center gap-3 p-3 bg-blue-50 border border-blue-200 rounded-md text-blue-800 text-sm">
                <ShieldCheck className="w-5 h-5 flex-shrink-0" />
                Digite o código de 6 dígitos do seu app autenticador ou um código de recuperação.
              </div>
              <div className="space-y-2">
                <Label htmlFor="mfa-code" className="text-gray-700 font-semibold text-sm">Código de verificação</Label>
                <Input
                  id="mfa-code"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  placeholder="000000"
                  value={mfaCode}
                  onChange={(e) => setMfaCode(e.target.value)}
                  className="h-11 bg-white border-gray-300 text-gray-900 text-center text-lg tracking-[0.4em] placeholder:tracking-[0.4em] focus:border-[#003366] focus:ring-[#003366]"
                  data-testid="mfa-code-input"
                  autoFocus
                  required
                />
              </div>
              <Button
                type="submit"
                disabled={loading || !mfaCode.trim()}
                data-testid="mfa-verify-btn"
                className="w-full h-11 bg-[#003366] hover:bg-[#002244] text-white font-semibold"
              >
                {loading ? 'Verificando...' : 'Verificar'}
              </Button>
              <button
                type="button"
                onClick={backToCredentials}
                data-testid="mfa-back-btn"
                className="w-full flex items-center justify-center gap-2 text-sm text-gray-500 hover:text-gray-700"
              >
                <ArrowLeft className="w-4 h-4" />
                Voltar ao login
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
};

export default Login;
